import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  UploadDetailConfig,
  UploadDetailConfigFormData,
} from '../models/upload-config.model';

// Legacy interfaces for migration
interface OldUploadDetailConfig {
  id?: string;
  group: string;
  path: string;
  fileCategory: string;
  imageType?: string;
  fileFormat: string[];
  fileSizeLimit: number;
  sizeUnit: 'KB' | 'MB' | 'GB';
  maxDetailImages: number;
  createdAt?: Date;
  updatedAt?: Date;
}

@Injectable({
  providedIn: 'root',
})
export class UploadDetailConfigService {
  private readonly STORAGE_KEY = 'upload-detail-configurations';
  private readonly configsSubject = new BehaviorSubject<UploadDetailConfig[]>(
    []
  );

  constructor() {
    this.initializeMigration();
    this.initializeData();
  }

  /**
   * Initialize data from localStorage or start with empty array
   */
  private initializeData(): void {
    const storedConfigs = this.getStoredConfigs();
    this.configsSubject.next(storedConfigs);
  }

  /**
   * Get all configurations as observable
   */
  getAll(): Observable<UploadDetailConfig[]> {
    return this.configsSubject.asObservable();
  }

  /**
   * Get configuration by page and field
   */
  getByPageAndField(page: string, field: string): UploadDetailConfig | null {
    const configs = this.configsSubject.value;
    return (
      configs.find(
        (config) => config.page === page && config.field === field
      ) || null
    );
  }

  /**
   * Get configurations by page
   */
  getByPage(page: string): UploadDetailConfig[] {
    const configs = this.configsSubject.value;
    return configs.filter((config) => config.page === page);
  }

  /**
   * Add or update configuration
   */
  addOrUpdate(configData: UploadDetailConfigFormData): void {
    const configs = this.configsSubject.value;
    let existingIndex = -1;

    if (configData.id) {
      // If an ID is provided, it's an edit operation - find by ID
      existingIndex = configs.findIndex(
        (config) => config.id === configData.id
      );
    } else {
      // Otherwise, it's a new creation - check for duplicates by page and field
      existingIndex = configs.findIndex(
        (config) =>
          config.page === configData.page && config.field === configData.field
      );
    }

    const now = new Date().toISOString();
    const config: UploadDetailConfig = {
      id: existingIndex >= 0 ? configs[existingIndex].id : this.generateId(),
      page: configData.page,
      field: configData.field,
      fileCategory: configData.fileCategory,
      fileFormat: configData.fileFormat,
      fileSizeLimit: configData.fileSizeLimit,
      sizeUnit: configData.sizeUnit,
      maxDetailImages: configData.maxDetailImages,
      createdAt: existingIndex >= 0 ? configs[existingIndex].createdAt : now,
      updatedAt: now,
    };

    if (existingIndex >= 0) {
      // Update existing
      configs[existingIndex] = config;
    } else {
      // Add new
      configs.push(config);
    }

    this.configsSubject.next([...configs]);
    this.syncToLocalStorage();
  }

  /**
   * Delete configuration by ID
   */
  delete(id: string): void {
    const configs = this.configsSubject.value;
    const filteredConfigs = configs.filter((config) => config.id !== id);
    this.configsSubject.next(filteredConfigs);
    this.syncToLocalStorage();
  }

  /**
   * Sync current configurations to localStorage
   */
  syncToLocalStorage(): void {
    const configs = this.configsSubject.value;
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(configs));
  }

  /**
   * Get configurations from localStorage
   */
  private getStoredConfigs(): UploadDetailConfig[] {
    try {
      const storedData = localStorage.getItem(this.STORAGE_KEY);
      if (!storedData) {
        return [];
      }
      return JSON.parse(storedData);
    } catch (error) {
      return [];
    }
  }

  /**
   * Generate unique ID
   */
  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
  }

  /**
   * Get configuration for current page and field (helper method)
   */
  getCurrentPageFieldConfig(
    page: string,
    field: string
  ): UploadDetailConfig | null {
    return this.getByPageAndField(page, field);
  }

  /**
   * Check if configuration exists for page and field
   */
  hasConfigForPageAndField(page: string, field: string): boolean {
    return this.getByPageAndField(page, field) !== null;
  }

  /**
   * Get all configurations synchronously (for immediate access)
   */
  getAllSync(): UploadDetailConfig[] {
    return this.configsSubject.value;
  }

  // ===== MIGRATION METHODS =====

  /**
   * Initialize migration on app startup
   */
  private initializeMigration(): void {
    this.migrateToNewFormat();

    // Create default configs if none exist
    const storedData = localStorage.getItem(this.STORAGE_KEY);
    if (!storedData || storedData === 'null' || storedData === 'undefined') {
      this.createDefaultConfigurations();
    } else {
      try {
        const parsedData = JSON.parse(storedData);
        if (!Array.isArray(parsedData) || parsedData.length === 0) {
          this.createDefaultConfigurations();
        }
      } catch (error) {
        this.createDefaultConfigurations();
      }
    }
  }

  /**
   * Migrate old configuration format to new format
   */
  private migrateToNewFormat(): void {
    try {
      const storedData = localStorage.getItem(this.STORAGE_KEY);
      if (!storedData) {
        return;
      }

      const oldConfigs: OldUploadDetailConfig[] = JSON.parse(storedData);

      // Check if already in new format
      if (oldConfigs.length > 0 && 'page' in oldConfigs[0]) {
        return; // Already migrated
      }

      const newConfigs: UploadDetailConfig[] = oldConfigs.map((oldConfig) => {
        // Map old structure to new structure
        const page = this.mapGroupToPage(oldConfig.group);
        const field = this.mapImageTypeToField(oldConfig.imageType || '');

        return {
          id: oldConfig.id,
          page,
          field,
          fileCategory: oldConfig.fileCategory,
          fileFormat: oldConfig.fileFormat,
          fileSizeLimit: oldConfig.fileSizeLimit,
          sizeUnit: oldConfig.sizeUnit,
          maxDetailImages: oldConfig.maxDetailImages,
          createdAt: oldConfig.createdAt
            ? new Date(oldConfig.createdAt).toISOString()
            : new Date().toISOString(),
          updatedAt: oldConfig.updatedAt
            ? new Date(oldConfig.updatedAt).toISOString()
            : new Date().toISOString(),
        };
      });

      // Save migrated data
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(newConfigs));
    } catch (error) {
      // Migration failed silently
    }
  }

  /**
   * Map old group to new page
   */
  private mapGroupToPage(group: string): string {
    if (group.includes('product-asset')) {
      return 'product-asset';
    }
    if (group.includes('order-asset')) {
      return 'order-asset';
    }
    if (group.includes('user-asset')) {
      return 'user-asset';
    }
    // Default fallback for unknown groups
    return 'product-asset';
  }

  /**
   * Map old imageType to new field
   */
  private mapImageTypeToField(imageType: string): string {
    const normalizedType = imageType.toLowerCase().trim();
    switch (normalizedType) {
      case 'detail images':
      case 'detailimages':
        return 'detail-images';
      case 'main image':
      case 'mainimage':
        return 'main-image';
      case 'thumbnail':
        return 'thumbnail';
      case 'banner':
        return 'banner';
      default:
        // Default fallback for unknown image types
        return 'detail-images';
    }
  }

  /**
   * Create default configurations if none exist
   */
  private createDefaultConfigurations(): void {
    const defaultConfigs: UploadDetailConfig[] = [
      {
        id: 'mf3nqa8s1j8va20sj8p',
        page: 'product-asset',
        field: 'detail-images',
        fileCategory: 'IMAGE',
        fileFormat: ['jpg', 'jpeg', 'png', 'webp', 'bmp', 'svg'],
        fileSizeLimit: 2,
        sizeUnit: 'MB',
        maxDetailImages: 36,
        createdAt: '2025-09-03T07:30:01.180Z',
        updatedAt: '2025-09-08T09:48:11.192Z',
      },
      {
        id: 'mf3nqa8s1j8va20sj8p2',
        page: 'product-asset',
        field: 'main-image',
        fileCategory: 'IMAGE',
        fileFormat: ['jpg', 'jpeg', 'png', 'webp', 'bmp', 'svg'],
        fileSizeLimit: 2,
        sizeUnit: 'MB',
        maxDetailImages: 1, // Always 1 for main image
        createdAt: '2025-09-03T07:30:01.180Z',
        updatedAt: '2025-09-08T09:48:11.192Z',
      },
    ];

    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(defaultConfigs));
  }
}
